'use client'

import { useUserProfile } from '@/hooks/use-user'
import { createContext, useState, useEffect, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'


interface NewsletterPopupContextType {
    closeModal: () => void
}

const NewsletterPopupContext = createContext<NewsletterPopupContextType | undefined>(undefined)

export function NewsletterPopupProvider({ children }: { children: React.ReactNode }) {
    const [modalVisible, setModalVisible] = useState<boolean>(false)

    const { userProfile, updateCommunicationPreferences } = useUserProfile()

    const userKey = useMemo(() => {
        return `newsletter_popup_${userProfile?.id}`
    }, [userProfile])

    const updateModalVisibility = useCallback(() => {
        if (!userProfile) {
            return
        }

        const value = localStorage.getItem(userKey)

        if (value) {
            return
        }

        if (userProfile.registeredWith !== 'GOOGLE' && userProfile.communicationPreferences.recibirNewsRTS) {
            return
        }

        setModalVisible(true)
    }, [userKey, userProfile])

    useEffect(() => {
        updateModalVisibility()
    }, [updateModalVisibility])

    const closeModal = () => {
        setModalVisible(false)
    }

    const updateValue = (value: boolean) => {
        updateCommunicationPreferences({ recibirNewsRTS: value })
        localStorage.setItem(userKey, value.toString())
    }

    return (
        <NewsletterPopupContext.Provider value={{ closeModal }}>
            {children}

            {
                typeof window !== 'undefined' && modalVisible &&
                createPortal(
                    <NewsletterPopupModal onClose={closeModal} updateValue={updateValue} />,
                    document.body
                )
            }
        </NewsletterPopupContext.Provider>
    )
}

const NewsletterPopupModal = ({ onClose, updateValue }: { onClose: () => void, updateValue: (value: boolean) => void }) => {
    const [bellRotation, setBellRotation] = useState<number>(0)

    // Animación de la campana
    useEffect(() => {
        const interval = setInterval(() => {
            setBellRotation(prev => prev === -10 ? 10 : -10)
        }, 500)

        return () => clearInterval(interval)
    }, [])

    const handleSubscribe = () => {
        updateValue(true)
        onClose()
    }

    const handleSkip = () => {
        updateValue(false)
        onClose()
    }

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center">
            {/* Overlay oscuro */}
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                aria-hidden="true"
                onClick={onClose}
            />

            {/* Modal centrado */}
            <div className="relative bg-white dark:bg-card rounded-xl shadow-xl p-8 w-full max-w-lg mx-4 z-10 text-center">
                {/* Campana animada */}
                <div className="mb-6">
                    <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#56ACDE"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mx-auto transition-transform duration-300"
                        style={{ transform: `rotate(${bellRotation}deg)` }}
                    >
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                        <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                </div>

                {/* Texto de valor */}
                <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
                    ¿Te unes a Rocker@ Insider?
                </h2>
                <p className="text-base text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                    Recibe cada semana noticias deportivas, eventos destacados y descuentos exclusivos solo para miembros Rocker@ Insider.
                </p>

                {/* CTA principal */}
                <button
                    onClick={handleSubscribe}
                    className="bg-primary hover:bg-primary-700 text-white px-8 py-3 rounded-lg text-base font-medium transition-colors duration-200 mb-6 w-full sm:w-auto"
                >
                    ✅ Sí, quiero recibir el newsletter
                </button>

                {/* Opción secundaria */}
                <div className="mt-6">
                    <button
                        onClick={handleSkip}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline transition-colors duration-200"
                    >
                        No gracias, continuar navegando
                    </button>
                </div>
            </div>
        </div>
    )
}